import React, { useState, useMemo } from 'react';
import { useDomains } from '@/hooks/useDomains';
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, Clock, RefreshCw } from 'lucide-react';
import { DomainTable } from './DomainTable';
import { DomainAnalyticsSection } from './DomainAnalyticsSection';
import { DomainMetrics } from './DomainMetrics';
import { BulkOperationsList } from '@/components/mailbox-management/BulkOperationsList';
import { AdvancedFilter, FilterRule, FieldConfig } from '@/components/ui/advanced-filter';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useDashboard } from '@/contexts/DashboardContext';
import { useClients } from '@/hooks/useClients';
const DOMAIN_FILTER_FIELDS: FieldConfig[] = [
  // Basic Domain Info
  {
    key: 'domain',
    label: 'Domain',
    type: 'text'
  }, {
    key: 'client_name',
    label: 'Client',
    type: 'select',
    options: [{
      value: 'Bayezon',
      label: 'Bayezon'
    }, {
      value: 'Cruva',
      label: 'Cruva'
    }, {
      value: 'Duel',
      label: 'Duel'
    }, {
      value: 'GFD / Agital',
      label: 'GFD / Agital'
    }, {
      value: 'Loop',
      label: 'Loop'
    }, {
      value: 'StoryStream',
      label: 'StoryStream'
    }, {
      value: 'Yotpo',
      label: 'Yotpo'
    }]
  }, {
    key: 'email_provider',
    label: 'Email Provider',
    type: 'text'
  }, {
    key: 'mailbox_provider',
    label: 'Mailbox Provider',
    type: 'text'
  }, {
    key: 'domain_registrar',
    label: 'Domain Registrar',
    type: 'text'
  },
  
  // Status Fields
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    options: [{
      value: 'Active',
      label: 'Active'
    }, {
      value: 'Inactive',
      label: 'Inactive'
    }, {
      value: 'Suspended',
      label: 'Suspended'
    }]
  }, {
    key: 'redirect_status',
    label: 'Redirect Status',
    type: 'select',
    options: [{
      value: 'Active',
      label: 'Active'
    }, {
      value: 'Inactive',
      label: 'Inactive'
    }, {
      value: 'Pending',
      label: 'Pending'
    }]
  }, {
    key: 'clickup_status',
    label: 'ClickUp Status',
    type: 'boolean'
  }, {
    key: 'mastersheet_status',
    label: 'Mastersheet Status',
    type: 'boolean'
  }, {
    key: 'smartlead_status',
    label: 'In Smartlead',
    type: 'boolean'
  }, {
    key: 'auto_renewal',
    label: 'Auto Renewal',
    type: 'boolean'
  },
  
  // Performance Metrics
  {
    key: 'health_score',
    label: 'Health Score',
    type: 'number'
  }, {
    key: 'reputation_score',
    label: 'Reputation Score',
    type: 'number'
  }, {
    key: 'bounce_rate',
    label: 'Bounce Rate',
    type: 'number'
  }, {
    key: 'daily_limit',
    label: 'Daily Limit',
    type: 'number'
  }, {
    key: 'current_daily_sent',
    label: 'Current Daily Sent',
    type: 'number'
  }, {
    key: 'total_emails_sent',
    label: 'Total Emails Sent',
    type: 'number'
  }, {
    key: 'total_email_bounces',
    label: 'Total Email Bounces',
    type: 'number'
  }, {
    key: 'total_email_replies',
    label: 'Total Email Replies',
    type: 'number'
  },
  
  // Mailbox Counts
  {
    key: 'total_mailboxes',
    label: 'Total Mailboxes',
    type: 'number'
  }, {
    key: 'active_mailboxes',
    label: 'Active Mailboxes',
    type: 'number'
  },
  
  // Dates
  {
    key: 'purchase_date',
    label: 'Purchase Date',
    type: 'date'
  }, {
    key: 'renewal_date',
    label: 'Renewal Date',
    type: 'date'
  }, {
    key: 'expiry_date',
    label: 'Expiry Date',
    type: 'date'
  },
  
  // URLs and References
  {
    key: 'redirect_url',
    label: 'Redirect URL',
    type: 'text'
  }
];
export function DomainDashboard() {
  const {
    data: domains = [],
    isLoading,
    refetch
  } = useDomains();
  const { state } = useDashboard();
  const { data: clients = [] } = useClients();
  const selectedClientId = state.selectedClient;
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterRule[]>([]);
  const [lastGlobalSync, setLastGlobalSync] = useState<string>('');
  const {
    toast
  } = useToast();
  const handleRenewalFilterClick = (filterType: 'renewing' | 'expiring') => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    if (filterType === 'renewing') {
      // Filter for domains renewing in next 30 days with auto_renewal = yes
      const today = new Date();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      const startFilter: FilterRule = {
        id: 'renewal-start-filter',
        field: 'renewal_date',
        operator: 'greater_equal',
        value: today.toISOString().split('T')[0]
      };
      const endFilter: FilterRule = {
        id: 'renewal-end-filter',
        field: 'renewal_date',
        operator: 'less_equal',
        value: thirtyDaysFromNow.toISOString().split('T')[0],
        connector: 'AND'
      };
      const autoRenewalFilter: FilterRule = {
        id: 'auto-renewal-yes-filter',
        field: 'auto_renewal',
        operator: 'equals',
        value: 'true',
        connector: 'AND'
      };
      setFilters([startFilter, endFilter, autoRenewalFilter]);
    } else if (filterType === 'expiring') {
      // Filter for domains expiring in next 30 days with auto_renewal = false
      const today = new Date();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      const todayFilter: FilterRule = {
        id: 'expiring-today-filter',
        field: 'expiry_date',
        operator: 'greater_equal',
        value: today.toISOString().split('T')[0]
      };
      const futureFilter: FilterRule = {
        id: 'expiring-future-filter',
        field: 'expiry_date',
        operator: 'less_equal',
        value: thirtyDaysFromNow.toISOString().split('T')[0],
        connector: 'AND'
      };
      const autoRenewalFilter: FilterRule = {
        id: 'auto-renewal-filter',
        field: 'auto_renewal',
        operator: 'equals',
        value: 'false',
        connector: 'AND'
      };
      setFilters([todayFilter, futureFilter, autoRenewalFilter]);
    }
  };
  const handleNoMailboxFilterClick = () => {
    // Filter for active domains with no mailboxes
    const newFilter: FilterRule = {
      id: 'no-mailbox-filter',
      field: 'total_mailboxes',
      operator: 'equals',
      value: '0',
      connector: 'AND'
    };
    const statusFilter: FilterRule = {
      id: 'status-filter',
      field: 'status',
      operator: 'equals',
      value: 'Active'
    };
    setFilters([statusFilter, newFilter]);
  };

  const handleAutoRenewalFilterClick = (autoRenewal: boolean) => {
    // Filter for domains with auto_renewal = true or false
    const newFilter: FilterRule = {
      id: 'auto-renewal-filter',
      field: 'auto_renewal',
      operator: 'equals',
      value: autoRenewal.toString()
    };
    setFilters([newFilter]);
  };

  const handleActiveStatusFilterClick = (status: 'active' | 'non-active') => {
    // Filter for active or non-active domains
    const newFilter: FilterRule = {
      id: 'active-status-filter',
      field: 'status',
      operator: 'equals',
      value: status === 'active' ? 'Active' : 'Inactive'
    };
    setFilters([newFilter]);
  };

  const handleChartFilter = (filterType: string, filterValue: string) => {
    // Create a new filter rule based on the chart click
    const newFilter: FilterRule = {
      id: `chart-filter-${Date.now()}`,
      field: filterType,
      operator: filterValue === 'Unknown' ? 'is_empty' : 'equals',
      value: filterValue === 'Unknown' ? '' : filterValue,
      connector: 'AND'
    };

    // If it's a purchase date filter, handle the date range
    if (filterType === 'purchase_date' && filterValue !== 'Unknown') {
      const [year, month] = filterValue.split('-');
      const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
      const endDate = new Date(parseInt(year), parseInt(month), 0);
      
      const startFilter: FilterRule = {
        id: `chart-date-start-${Date.now()}`,
        field: 'purchase_date',
        operator: 'greater_equal',
        value: startDate.toISOString().split('T')[0],
        connector: 'AND'
      };
      
      const endFilter: FilterRule = {
        id: `chart-date-end-${Date.now()}`,
        field: 'purchase_date',
        operator: 'less_equal',
        value: endDate.toISOString().split('T')[0],
        connector: 'AND'
      };
      
      setFilters([startFilter, endFilter]);
    } else {
      setFilters([newFilter]);
    }
  };

  // Get last global sync time
  React.useEffect(() => {
    const getLastSync = async () => {
      const {
        data
      } = await supabase.from('domains').select('updated_at').order('updated_at', {
        ascending: false
      }).limit(1).single();
      if (data?.updated_at) {
        setLastGlobalSync(data.updated_at);
      }
    };
    getLastSync();
  }, []);
  const applyFilters = (data: any[], query: string, filterRules: FilterRule[]) => {
    let filtered = data;

    // Apply search query
    if (query) {
      filtered = filtered.filter(item => item.domain?.toLowerCase().includes(query.toLowerCase()) || item.client_name?.toLowerCase().includes(query.toLowerCase()) || item.email_provider?.toLowerCase().includes(query.toLowerCase()) || item.domain_registrar?.toLowerCase().includes(query.toLowerCase()));
    }

    // Apply advanced filters
    if (filterRules.length > 0) {
      filtered = filtered.filter(item => {
        return evaluateFilterGroup(item, filterRules);
      });
    }
    return filtered;
  };
  const evaluateFilterGroup = (item: any, rules: FilterRule[]) => {
    if (rules.length === 0) return true;
    let result = evaluateRule(item, rules[0]);
    for (let i = 1; i < rules.length; i++) {
      const rule = rules[i];
      const ruleResult = evaluateRule(item, rule);
      if (rule.connector === 'OR') {
        result = result || ruleResult;
      } else {
        result = result && ruleResult;
      }
    }
    return result;
  };
  const evaluateRule = (item: any, rule: FilterRule) => {
    const value = item[rule.field];
    const filterValue = rule.value;
    
    // Handle date fields specifically
    if (['renewal_date', 'expiry_date', 'purchase_date'].includes(rule.field)) {
      if (!value) return false;
      const itemDate = new Date(value);
      const filterDate = new Date(filterValue);
      
      switch (rule.operator) {
        case 'greater_than':
          return itemDate > filterDate;
        case 'less_than':
          return itemDate < filterDate;
        case 'greater_equal':
          return itemDate >= filterDate;
        case 'less_equal':
          return itemDate <= filterDate;
        case 'equals':
          return itemDate.toDateString() === filterDate.toDateString();
        case 'not_equals':
          return itemDate.toDateString() !== filterDate.toDateString();
        default:
          return String(value || '').toLowerCase().includes(String(filterValue).toLowerCase());
      }
    }
    
    // Handle other fields
    switch (rule.operator) {
      case 'contains':
        return String(value || '').toLowerCase().includes(String(filterValue).toLowerCase());
      case 'equals':
        return String(value) === String(filterValue);
      case 'starts_with':
        return String(value || '').toLowerCase().startsWith(String(filterValue).toLowerCase());
      case 'ends_with':
        return String(value || '').toLowerCase().endsWith(String(filterValue).toLowerCase());
      case 'not_contains':
        return !String(value || '').toLowerCase().includes(String(filterValue).toLowerCase());
      case 'is_empty':
        return !value || value === '';
      case 'is_not_empty':
        return value && value !== '';
      case 'greater_than':
        return Number(value) > Number(filterValue);
      case 'less_than':
        return Number(value) < Number(filterValue);
      case 'greater_equal':
        return Number(value) >= Number(filterValue);
      case 'less_equal':
        return Number(value) <= Number(filterValue);
      case 'not_equals':
        return String(value) !== String(filterValue);
      default:
        return true;
    }
  };
  // Apply global client filter from header dropdown
  const clientFilteredDomains = useMemo(() => {
    if (!selectedClientId || selectedClientId === 'all') return domains;
    const selectedClient = clients.find((c: any) => c.id === selectedClientId);
    if (!selectedClient) return domains;
    return domains.filter((d: any) => d.client_name === selectedClient.name);
  }, [domains, selectedClientId, clients]);

  const filteredDomains = useMemo(() => {
    return applyFilters(clientFilteredDomains, searchQuery, filters);
  }, [clientFilteredDomains, searchQuery, filters]);
  const formatLastSync = (lastSync: string) => {
    if (!lastSync) return 'Never';
    try {
      const date = new Date(lastSync);
      if (isNaN(date.getTime()) || date > new Date()) return 'Never';
      return date.toLocaleString();
    } catch {
      return 'Never';
    }
  };
  const handleRefresh = () => {
    refetch();
    toast({
      title: "Refreshed",
      description: "Domain data has been refreshed"
    });
  };
  return <div className="space-y-6">{/* Domain Dashboard - No header should appear here */}

      {/* Metrics Section */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-8">
          <DomainMetrics
            domains={clientFilteredDomains}
            onRenewalFilterClick={handleRenewalFilterClick}
            onNoMailboxFilterClick={handleNoMailboxFilterClick}
            onActiveStatusFilterClick={handleActiveStatusFilterClick}
          />
        </div>
      </div>

      {/* Analytics Section */}
      <DomainAnalyticsSection domains={filteredDomains} onFilterChange={handleChartFilter} />

      {/* Table Section */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <DomainTable 
          filteredDomains={filteredDomains} 
          isLoading={isLoading} 
          onUpdate={refetch}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filters={filters}
          onFiltersChange={setFilters}
          filterFields={DOMAIN_FILTER_FIELDS}
        />
      </div>
    </div>;
}